import { useCreateEmailVerification, useVerifyEmail } from "@/tanstack-hooks";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Input } from "../ui/input";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2Icon, MailIcon, PencilIcon } from "lucide-react";
import { useState } from "react";
import { ApiError } from "@/generated-api/core/ApiError";
import { Label } from "../ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import { REGEXP_ONLY_DIGITS_AND_LETTERS } from "../auth/LoginOtpCard";
import { ErrorResponse } from "@/generated-api/models/ErrorResponse";
import { useToast } from "@/hooks/use-toast";
import { AccountSettingsResponse } from "@/generated-api/models/AccountSettingsResponse";
import { useTranslation } from "@/hooks/useTranslation";

const emailFormSchema = z.object({
  email: z.string().email(),
});

enum UpdateEmailStep {
  VIEW = 1,
  ENTER_NEW_EMAIL = 2,
  CONFIRM_OTP = 3,
}

export default function UpdateEmailSection({
  accountSettings,
}: {
  accountSettings: AccountSettingsResponse;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<UpdateEmailStep>(UpdateEmailStep.VIEW);
  const [newEmail, setNewEmail] = useState<string | null>(null);
  const [otp, setOTP] = useState<string | null>(null);
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const t = useTranslation();

  const emailForm = useForm<z.infer<typeof emailFormSchema>>({
    resolver: zodResolver(emailFormSchema),
  });

  const createVerificationMutation = useCreateEmailVerification();
  const verifyEmailMutation = useVerifyEmail();

  function handleSendCode(values: z.infer<typeof emailFormSchema>) {
    createVerificationMutation.mutate(
      { email: values.email.toLocaleLowerCase() },
      {
        onSuccess: () => {
          emailForm.clearErrors();
          setStep(UpdateEmailStep.CONFIRM_OTP);
          setNewEmail(values.email.toLocaleLowerCase());
          toast({
            variant: "default",
            // @ts-expect-error Type 'Element' is not assignable to type
            title: (
              <div className="flex items-center gap-2">
                <MailIcon className="h-4 w-4" />
                <span>{t.EMAIL_SETTINGS.LABELS.EMAILED_CODE_SUCCESS}</span>
              </div>
            ),
            className: "dark rounded-2xl",
            duration: 2000,
          });
        },
        onError: (error) => {
          const err = error as ApiError;
          if (err.status === 409) {
            emailForm.setError("email", {
              type: "manual",
              message: t.EMAIL_SETTINGS.ERRORS.EMAIL_UPDATE_CONFLICT,
            });
          } else if (err.status === 400) {
            emailForm.setError("email", {
              type: "manual",
              message: t.EMAIL_SETTINGS.ERRORS.EMAIL_UPDATE_INVALID,
            });
          } else {
            emailForm.setError("email", {
              type: "manual",
              message: t.EMAIL_SETTINGS.ERRORS.EMAIL_UPDATE_ERROR,
            });
          }
        },
      }
    );
  }

  function handleVerifyEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail || !otp) {
      return;
    }
    verifyEmailMutation.mutate(
      { email: newEmail, otp_code: otp },
      {
        onSuccess: () => {
          setSendOtpError(null);
          handleCancel();
          toast({
            variant: "default",
            // @ts-expect-error Type 'Element' is not assignable to type
            title: (
              <div className="flex items-center gap-2">
                <CheckCircle2Icon className="h-4 w-4" />
                <span>{t.EMAIL_SETTINGS.LABELS.EMAIL_UPDATED_SUCCESS}</span>
              </div>
            ),
            className: "dark rounded-2xl",
            duration: 1500,
          });
        },
        onError: (error) => {
          const err = error as ApiError;
          if (err.status === 400) {
            const b = err.body as ErrorResponse;
            setSendOtpError(b.detail);
          } else {
            setSendOtpError(t.EMAIL_SETTINGS.ERRORS.EMAIL_UPDATE_ERROR);
          }
        },
      }
    );
  }

  function handleCancel() {
    setStep(UpdateEmailStep.VIEW);
    emailForm.clearErrors();
    emailForm.reset();
    setNewEmail(null);
    setOTP(null);
    setSendOtpError(null);
  }

  const handleOTPChange = (value: string) => {
    setOTP(value.toUpperCase());
  };

  return (
    <Card className="dark w-full max-w-lg">
      <CardHeader>
        <CardTitle className="text-xl">
          {t.EMAIL_SETTINGS.LABELS.UPDATE_EMAIL_TITLE}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {step === UpdateEmailStep.VIEW ? (
          <div className="flex flex-wrap gap-4">
            <Input
              className="w-fit"
              disabled={true}
              value={accountSettings.email}
            />
            <Button onClick={() => setStep(UpdateEmailStep.ENTER_NEW_EMAIL)}>
              <PencilIcon className="h-4 w-4 mr-2" />
              {t.SHARED.LABELS.EDIT_BUTTON}
            </Button>
          </div>
        ) : step === UpdateEmailStep.ENTER_NEW_EMAIL ? (
          <Form {...emailForm}>
            <form
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={emailForm.handleSubmit(handleSendCode)}
              className="flex flex-wrap gap-4"
            >
              <FormField
                control={emailForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        className="w-fit text-base"
                        placeholder={
                          t.EMAIL_SETTINGS.LABELS.NEW_EMAIL_PLACEHOLDER
                        }
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={createVerificationMutation.isPending}
              >
                {createVerificationMutation.isPending
                  ? t.SHARED.LABELS.SENDING_BUTTON
                  : t.EMAIL_SETTINGS.LABELS.SEND_CODE_BUTTON}
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                {t.SHARED.LABELS.CANCEL_BUTTON}
              </Button>
            </form>
          </Form>
        ) : (
          <form onSubmit={handleVerifyEmail} className="gap-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <Label htmlFor="otp">
                  {t.EMAIL_SETTINGS.LABELS.ONE_TIME_CODE}
                </Label>
                <InputOTP
                  maxLength={6}
                  value={otp ? otp : ""}
                  onChange={handleOTPChange}
                  inputMode="text"
                  pattern={REGEXP_ONLY_DIGITS_AND_LETTERS}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button type="submit" disabled={!newEmail}>
                {t.SHARED.LABELS.CONFIRM_BUTTON}
              </Button>
              <Button variant="secondary" onClick={handleCancel}>
                {t.SHARED.LABELS.CANCEL_BUTTON}
              </Button>
            </div>
            {sendOtpError && (
              <div className="text-sm font-medium text-destructive">
                {sendOtpError}
              </div>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
}
